import React, { useState } from "react";
import { Services } from "../../utilities/models/ServiceModel";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import PagesEndpoints from "../../utilities/constants/PagesEndpoints";
import { RESERVATIONS } from "../../utilities/constants/AccountScreen";
import KeycloakService from "../config/KeycloakService";
import Modal from "../../utilities/components/Modal";
import LocalizedModal from "../../utilities/components/LocalizedModal";
import { useUserRefresh } from "../user/UserProvider";

// Color mapping for service cards
const colorClasses = {
  rose: "bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 group-hover:bg-rose-600",
  pink: "bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 group-hover:bg-pink-600",
  amber:
    "bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 group-hover:bg-amber-600",
  purple:
    "bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 group-hover:bg-purple-600",
  blue: "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 group-hover:bg-blue-600",
  indigo:
    "bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 group-hover:bg-indigo-600",
  red: "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 group-hover:bg-red-600",
  slate:
    "bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-400 group-hover:bg-slate-600",
  cyan: "bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 group-hover:bg-cyan-600",
  teal: "bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 group-hover:bg-teal-600",
  yellow:
    "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 group-hover:bg-yellow-600",
  emerald:
    "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 group-hover:bg-emerald-600",
  orange:
    "bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 group-hover:bg-orange-600",
  violet:
    "bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 group-hover:bg-violet-600",
};

const ServicesOverview = () => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const setFetchUserFlag = useUserRefresh();

  // Featured services - let's pick the first 3 or specific ones
  const featuredServices = Services.slice(0, 3);

  const handleBookNow = (serviceTitle) => {
    if (KeycloakService.isLoggedIn()) {
      navigate(`${PagesEndpoints.BOOKING_PAGE_URL}/${RESERVATIONS}`, {
        state: { eventType: serviceTitle },
      });
    } else {
      setShowLoginModal(true);
    }
  };

  const handleLogin = async () => {
    await KeycloakService.doLogin(setFetchUserFlag);
  };

  return (
    <section className="py-20 bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <div className="container mx-auto px-6 max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <span className="text-sm font-bold tracking-widest text-blue-600 dark:text-blue-400 uppercase mb-2 block">
            Our Expertise
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4 font-serif">
            Curated Experiences
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-lg">
            From intimate gatherings to grand celebrations, we provide the
            perfect setting for every occasion.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {featuredServices.map((service) => {
            const Icon = service.icon;
            const colorClass = colorClasses[service.color] || colorClasses.blue;

            return (
              <div
                key={service.id}
                className="group relative bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100 dark:border-slate-700 hover:-translate-y-1"
              >
                {/* Icon Area */}
                <div className="pt-10 pb-6 px-8 flex justify-center">
                  <div
                    className={`p-6 rounded-2xl transition-all duration-300 group-hover:text-white group-hover:scale-110 ${colorClass}`}
                  >
                    {Icon && <Icon size={48} strokeWidth={1.5} />}
                  </div>
                </div>

                {/* Content Area */}
                <div className="px-8 pb-8 text-center">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-3 font-serif">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-6 line-clamp-3">
                    {service.description}
                  </p>

                  <button
                    onClick={() => handleBookNow(service.title)}
                    className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-semibold group-hover:gap-3 transition-all"
                  >
                    Book This Event <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Details */}
        <div className="mt-12 text-center">
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate(PagesEndpoints.SERVICES_PAGE_URL);
            }}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-full border-2 border-slate-900 dark:border-white text-slate-900 dark:text-white font-bold tracking-wide uppercase hover:bg-slate-900 hover:text-white dark:hover:bg-white dark:hover:text-slate-900 transition-colors"
          >
            View All Services
          </button>
        </div>
      </div>

      {/* Login Modal for Booking */}
      <Modal isOpen={showLoginModal} onClose={setShowLoginModal}>
        <LocalizedModal
          actionHandler={handleLogin}
          actionText="Sign In"
          cancelText="Cancel"
          message="Please sign in to book an event."
          setOpen={setShowLoginModal}
          title="Sign In Required"
        />
      </Modal>
    </section>
  );
};

export default ServicesOverview;
